import React, { useState, useEffect } from 'react';
import { parseLocalDate, formatDate } from '../utils/dateUtils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { FileText, Calendar, DollarSign, Plus, Edit2, Trash2, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Download, Users, Search, MessageSquare, Building2 } from 'lucide-react';
import { calculateLoanCommission, calculateProjectedCommission, getCurrentTier, getCommissionTiers } from '../utils/commissionTiers';
import { useAuth } from '../context/AuthContext';
import jsPDF from 'jspdf';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const LoanManagement = () => {
  const { isAdmin, isProcessor, user } = useAuth();
  const brokerType = user?.broker_type || 'senior';
  const [loans, setLoans] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingLoan, setEditingLoan] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [lmViewMode, setLmViewMode] = useState('month'); // 'month' or 'ytd'
  const [isLoaded, setIsLoaded] = useState(false);
  const [loanOfficers, setLoanOfficers] = useState([]);
  const [selectedOfficer, setSelectedOfficer] = useState('mine');
  const [showOfficerDropdown, setShowOfficerDropdown] = useState(false);
  const [sortField, setSortField] = useState('date'); // 'clientName', 'loanAmount', 'lender', 'date', 'status', 'commission', 'processor'
  const [sortOrder, setSortOrder] = useState('desc');

  // When admin views another officer, use that officer's broker type for commission calcs
  const effectiveBrokerType = (() => {
    if (!isAdmin || selectedOfficer === 'mine') return brokerType;
    if (selectedOfficer === 'all' || selectedOfficer === 'all_processing') return brokerType;
    const officer = loanOfficers.find(o => o.id === selectedOfficer);
    return officer?.broker_type || brokerType;
  })();

  // Processors default to company-wide view
  useEffect(() => {
    if (isProcessor) {
      setSelectedOfficer('all');
    }
  }, [isProcessor]);
  const [formErrors, setFormErrors] = useState({});
  const [fundingDateDialog, setFundingDateDialog] = useState({ open: false, loanId: null, fundingDate: '', closingDate: '' });
  const [pickerOpen, setPickerOpen] = useState(null); // 'lender' | 'leadSource' | 'processor' | 'state' | 'loanType' | null
  const [processorFilter, setProcessorFilter] = useState('');
  const [processorDropdownFilter, setProcessorDropdownFilter] = useState('all');
  const [formData, setFormData] = useState({
    clientName: '',
    loanNumber: '',
    loanAmount: '',
    baseCommissionRate: '2.75',
    commissionType: 'percent',
    lender: '',
    leadSource: '',
    processor: '',
    state: '',
    loanType: '',
    isSelfGenerated: false,
    status: '',
    submissionDate: '',  // When filled, auto-sets status to "In Process"
    date: new Date().toISOString().split('T')[0],
    fundingDate: '',
    reimbursement: '0',
    cure: '0',
    notes: '',
    referralSourceType: '',
    referrerName: '',
    referrerPhone: '',
  });

  // Processor options - fetched dynamically from DB
  const [processorOptions, setProcessorOptions] = useState(['Self Processing']);

  useEffect(() => {
    if (!user) return;
    const fetchProcessors = async () => {
      try {
        const response = await axios.get(`${API}/api/processors`);
        const names = response.data.map(p => p.name);
        setProcessorOptions(['Self Processing', ...names]);
      } catch (error) {
        console.error('Error fetching processors:', error);
      }
    };
    fetchProcessors();
  }, [user]);

  // State options
  const stateOptions = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
  ];

  // Loan type options
  const loanTypeOptions = [
    'Rate & Term',
    'Cash Out Refinance',
    'Purchase',
    'Fixed 2nd',
    'HELOC',
    'DSCR Purchase',
    'DSCR Refinance',
    'IRRRL',
    'FHA Streamline'
  ];

  // Format number with commas
  const formatNumberWithCommas = (value) => {
    if (!value) return '';
    // Remove any non-digit characters except decimal point
    const cleanValue = value.toString().replace(/[^\d.]/g, '');
    // Split into integer and decimal parts
    const parts = cleanValue.split('.');
    // Format integer part with commas
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };

  // Parse number removing commas
  const parseFormattedNumber = (value) => {
    if (!value) return '';
    return value.toString().replace(/,/g, '');
  };

  // Handle number input with comma formatting
  const handleNumberChange = (field, value) => {
    const cleanValue = parseFormattedNumber(value);
    // Only allow valid number characters
    if (cleanValue && !/^\d*\.?\d*$/.test(cleanValue)) return;
    setFormData({ ...formData, [field]: cleanValue });
  };

  // Lead source options
  const leadSourceOptions = [
    'Referral',
    'Conv Mailer',
    'FHA Mailer',
    'VA Mailer',
    'Trigger',
    'Past Client',
    'Lead Buy',
    'Outbound'
  ];

  const lenderOptions = [
    'UWM', 'Loan Depot', 'PennyMac', 'Sun West', 'Loan United', 'Figure',
    'EPM', 'Spring EQ', 'Newfi', 'A&D', 'ACRA', 'Freedom Mortgage', 'Rocket', 'TLS', 'AXOS', 'Newrez', 'Other'
  ];

  // Fetch loan officers list for admin/processor dropdown
  useEffect(() => {
    if (isAdmin || isProcessor) {
      const fetchOfficers = async () => {
        try {
          const response = await axios.get(`${API}/api/admin/loan-officers`);
          setLoanOfficers(response.data);
        } catch (error) {
          console.error('Error fetching loan officers:', error);
        }
      };
      fetchOfficers();
    }
  }, [isAdmin, isProcessor]);

  // Load loans from API on mount
  useEffect(() => {
    const fetchLoans = async () => {
      try {
        let url = `${API}/api/loans`;
        if (isAdmin || isProcessor) {
          // Check if selected user is a processor
          const isViewingProcessor = selectedOfficer !== 'mine' && selectedOfficer !== 'all' && 
            loanOfficers.find(o => o.id === selectedOfficer)?.role === 'processor';
          if (selectedOfficer === 'all' || selectedOfficer === 'all_processing' || isViewingProcessor) {
            url += '?user_id=all';
          } else if (selectedOfficer !== 'mine') {
            url += `?user_id=${selectedOfficer}`;
          }
        }
        // Processors automatically get all loans from backend
        const response = await axios.get(url);
        // Transform API response to match frontend format
        const transformedLoans = response.data.map(loan => ({
          id: loan.id,
          userId: loan.user_id,
          userName: loan.user_name,
          clientName: loan.client_name,
          loanNumber: loan.loan_number,
          loanAmount: loan.loan_amount,
          baseCommissionRate: loan.base_commission_rate,
          commissionType: loan.commission_type || 'percent',
          lender: loan.lender,
          leadSource: loan.lead_source,
          processor: loan.processor,
          state: loan.state,
          loanType: loan.loan_type,
          isSelfGenerated: loan.is_self_generated,
          status: loan.status,
          submissionDate: loan.submission_date,
          date: loan.date,
          fundingDate: loan.funding_date,
          reimbursement: loan.reimbursement,
          cure: loan.cure,
          notes: loan.notes,
          referralSourceType: loan.referral_source_type || '',
          referrerName: loan.referrer_name || '',
          referrerPhone: loan.referrer_phone || '',
        }));
        setLoans(transformedLoans);
      } catch (error) {
        console.error('Error fetching loans:', error);
        // Fallback to localStorage
        const savedLoans = localStorage.getItem('kinfolk_loans');
        if (savedLoans) {
          try {
            setLoans(JSON.parse(savedLoans));
          } catch (e) {
            console.error('Error loading loans:', e);
          }
        }
      }
      setIsLoaded(true);
    };
    fetchLoans();
  }, [isAdmin, isProcessor, selectedOfficer, loanOfficers]);

  const getMonthKey = (date) => {
    const d = parseLocalDate(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };

  const formatMonth = (date) => {
    return parseLocalDate(date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const selectedMonthKey = getMonthKey(selectedMonth);

  // Determine if admin is viewing a processor's profile (needed early for filtering)
  const selectedUserRole = loanOfficers.find(o => o.id === selectedOfficer)?.role;
  const viewingProcessor = !isProcessor && selectedOfficer !== 'mine' && selectedOfficer !== 'all' && selectedOfficer !== 'all_processing' && selectedUserRole === 'processor';
  const officersList = loanOfficers.filter(o => o.role !== 'processor');
  const processorsList = loanOfficers.filter(o => o.role === 'processor');

  const monthLoansRaw = loans.filter(loan => {
    if (lmViewMode === 'ytd') {
      const d = parseLocalDate(loan.date);
      return d.getFullYear() === selectedMonth.getFullYear();
    }
    return getMonthKey(loan.date) === selectedMonthKey;
  });
  
  // Apply processor's loan officer filter or admin's processor dropdown filter
  let monthLoans = monthLoansRaw;
  if ((isProcessor || viewingProcessor) && processorFilter) {
    monthLoans = monthLoansRaw.filter(l => l.userName?.toLowerCase().includes(processorFilter.toLowerCase()) || l.clientName?.toLowerCase().includes(processorFilter.toLowerCase()) || l.processor?.toLowerCase().includes(processorFilter.toLowerCase()));
  } else if (!(isProcessor || viewingProcessor) && processorDropdownFilter !== 'all') {
    monthLoans = monthLoansRaw.filter(l => l.processor === processorDropdownFilter);
  }
  // Sorting will be applied after calculateCommission is defined
  const closedMonthLoans = monthLoans.filter(l => l.status === 'Closed');

  const navigateMonth = (direction) => {
    const newDate = new Date(selectedMonth);
    newDate.setMonth(newDate.getMonth() + direction);
    setSelectedMonth(newDate);
  };

  const calculateCommission = (loan) => {
    if (loan.status !== 'Closed') return 0;
    if (lmViewMode === 'ytd') {
      // In YTD mode, calculate based on the loan's own month's closed count
      const loanMonthKey = getMonthKey(loan.date);
      const sameMonthClosed = loans.filter(l => l.status === 'Closed' && getMonthKey(l.date) === loanMonthKey);
      const baseCommission = calculateLoanCommission(loan, sameMonthClosed.length, effectiveBrokerType);
      const reimbursement = parseFloat(loan.reimbursement || 0);
      const cure = parseFloat(loan.cure || 0);
      return baseCommission + reimbursement - cure;
    }
    const monthClosedCount = closedMonthLoans.length;
    const baseCommission = calculateLoanCommission(loan, monthClosedCount, effectiveBrokerType);
    const reimbursement = parseFloat(loan.reimbursement || 0);
    const cure = parseFloat(loan.cure || 0);
    return baseCommission + reimbursement - cure;
  };

  // Sort loans after calculateCommission is available
  monthLoans = [...monthLoans].sort((a, b) => {
    let valA, valB;
    switch (sortField) {
      case 'clientName':
        valA = (a.clientName || '').toLowerCase();
        valB = (b.clientName || '').toLowerCase();
        return sortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      case 'loanAmount':
        valA = a.loanAmount || 0;
        valB = b.loanAmount || 0;
        return sortOrder === 'asc' ? valA - valB : valB - valA;
      case 'lender':
        valA = (a.lender || '').toLowerCase();
        valB = (b.lender || '').toLowerCase();
        return sortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      case 'status':
        valA = (a.status || '').toLowerCase();
        valB = (b.status || '').toLowerCase();
        return sortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      case 'commission':
        valA = calculateCommission(a);
        valB = calculateCommission(b);
        return sortOrder === 'asc' ? valA - valB : valB - valA;
      case 'processor':
        valA = (a.processor || '').toLowerCase();
        valB = (b.processor || '').toLowerCase();
        return sortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      case 'date':
      default:
        valA = parseLocalDate(a.date);
        valB = parseLocalDate(b.date);
        return sortOrder === 'asc' ? valA - valB : valB - valA;
    }
  });

  // Calculate estimated commission for In Process loans (doesn't count toward totals)
  const calculateEstimatedCommission = (loan) => {
    const estimatedMonthCount = closedMonthLoans.length + 1;
    return calculateProjectedCommission(loan, estimatedMonthCount, effectiveBrokerType);
  };

  const monthlyStats = {
    totalLoans: monthLoans.length,
    closedLoans: closedMonthLoans.length,
    inProcessLoans: monthLoans.filter(l => l.status === 'In Process' || l.status === 'Pending').length,
    withdrawnLoans: monthLoans.filter(l => l.status === 'Withdrawn').length,
    totalCommission: closedMonthLoans.reduce((sum, loan) => sum + calculateCommission(loan), 0),
    selfGenLoans: closedMonthLoans.filter(l => l.isSelfGenerated).length,
  };

  const monthTier = getCurrentTier(closedMonthLoans.length, effectiveBrokerType);

  const handleAddLoan = async () => {
    // Validate required fields
    const errors = {};
    if (!formData.clientName.trim()) {
      errors.clientName = 'Client Name is required';
    }
    if (!formData.loanNumber.trim()) {
      errors.loanNumber = 'Loan Number is required';
    }
    if (!formData.loanAmount || parseFloat(formData.loanAmount) <= 0) {
      errors.loanAmount = 'Loan Amount is required';
    }
    if (!formData.lender) {
      errors.lender = 'Lender is required';
    }
    if (!formData.leadSource) {
      errors.leadSource = 'Lead Source is required';
    }
    if (!formData.processor.trim()) {
      errors.processor = 'Processor is required';
    }
    if (!formData.state) {
      errors.state = 'State is required';
    }
    if (!formData.loanType) {
      errors.loanType = 'Loan Type is required';
    }
    if (!formData.baseCommissionRate || parseFloat(formData.baseCommissionRate) <= 0) {
      errors.baseCommissionRate = 'Base Commission Rate is required';
    }
    if (!formData.date) {
      errors.date = 'Closing Date is required';
    }
    if (!formData.submissionDate) {
      errors.submissionDate = 'Submission Date is required';
    }
    if (!formData.status) {
      errors.status = 'Status is required';
    }

    if (formData.leadSource === 'Referral') {
      if (!formData.referralSourceType) {
        errors.referralSourceType = 'Referral Source Type is required';
      }
      if (!formData.referrerName.trim()) {
        errors.referrerName = "Referrer's Name is required";
      }
      if (!formData.referrerPhone.trim()) {
        errors.referrerPhone = "Referrer's Phone Number is required";
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});

    // Auto-set status to "In Process" if submission date is provided
    const effectiveStatus = formData.submissionDate ? 'In Process' : formData.status;

    const loanPayload = {
      client_name: formData.clientName,
      loan_number: formData.loanNumber,
      loan_amount: parseFloat(formData.loanAmount),
      base_commission_rate: parseFloat(formData.baseCommissionRate),
      commission_type: formData.commissionType,
      lender: formData.lender,
      lead_source: formData.leadSource,
      processor: formData.processor,
      state: formData.state,
      loan_type: formData.loanType,
      is_self_generated: formData.isSelfGenerated,
      status: effectiveStatus,
      submission_date: formData.submissionDate || null,
      date: formData.date,
      funding_date: formData.fundingDate || null,
      reimbursement: parseFloat(formData.reimbursement || 0),
      cure: parseFloat(formData.cure || 0),
      notes: formData.notes,
      referral_source_type: formData.leadSource === 'Referral' ? formData.referralSourceType : null,
      referrer_name: formData.leadSource === 'Referral' ? formData.referrerName : null,
      referrer_phone: formData.leadSource === 'Referral' ? formData.referrerPhone : null,
    };

    try {
      if (editingLoan) {
        // Update existing loan
        await axios.put(`${API}/api/loans/${editingLoan.id}`, loanPayload);
        setLoans(loans.map(loan => 
          loan.id === editingLoan.id 
            ? {
                ...loan,
                clientName: formData.clientName,
                loanNumber: formData.loanNumber,
                loanAmount: parseFloat(formData.loanAmount),
                baseCommissionRate: parseFloat(formData.baseCommissionRate),
                commissionType: formData.commissionType,
                lender: formData.lender,
                leadSource: formData.leadSource,
                processor: formData.processor,
                state: formData.state,
                loanType: formData.loanType,
                isSelfGenerated: formData.isSelfGenerated,
                status: effectiveStatus,
                submissionDate: formData.submissionDate || null,
                date: formData.date,
                fundingDate: formData.fundingDate || null,
                reimbursement: parseFloat(formData.reimbursement || 0),
                cure: parseFloat(formData.cure || 0),
                notes: formData.notes,
                referralSourceType: formData.leadSource === 'Referral' ? formData.referralSourceType : '',
                referrerName: formData.leadSource === 'Referral' ? formData.referrerName : '',
                referrerPhone: formData.leadSource === 'Referral' ? formData.referrerPhone : '',
              }
            : loan
        ));
        setEditingLoan(null);
      } else {
        // Create new loan
        const response = await axios.post(`${API}/api/loans`, loanPayload);
        const newLoan = {
          id: response.data.id,
          clientName: response.data.client_name,
          loanNumber: response.data.loan_number,
          loanAmount: response.data.loan_amount,
          baseCommissionRate: response.data.base_commission_rate,
          commissionType: response.data.commission_type || 'percent',
          lender: response.data.lender,
          leadSource: response.data.lead_source,
          processor: response.data.processor,
          state: response.data.state,
          loanType: response.data.loan_type,
          isSelfGenerated: response.data.is_self_generated,
          status: response.data.status,
          submissionDate: response.data.submission_date,
          date: response.data.date,
          fundingDate: response.data.funding_date,
          reimbursement: response.data.reimbursement,
          cure: response.data.cure,
          notes: response.data.notes,
          referralSourceType: response.data.referral_source_type || '',
          referrerName: response.data.referrer_name || '',
          referrerPhone: response.data.referrer_phone || '',
        };
        setLoans([newLoan, ...loans]);
      }
    } catch (error) {
      console.error('Error saving loan:', error);
      alert('Error saving loan. Please try again.');
      return;
    }

    setFormData({
      clientName: '',
      loanNumber: '',
      loanAmount: '',
      baseCommissionRate: '2.75',
      commissionType: 'percent',
      lender: '',
      leadSource: '',
      processor: '',
      state: '',
      loanType: '',
      isSelfGenerated: false,
      status: '',
      submissionDate: '',
      date: new Date().toISOString().split('T')[0],
      fundingDate: '',
      reimbursement: '0',
      cure: '0',
      notes: '',
    });
    setIsDialogOpen(false);
  };

  const handleEditLoan = (loan) => {
    setEditingLoan(loan);
    setFormData({
      clientName: loan.clientName,
      loanNumber: loan.loanNumber || '',
      loanAmount: loan.loanAmount.toString(),
      baseCommissionRate: loan.baseCommissionRate.toString(),
      commissionType: loan.commissionType || 'percent',
      lender: loan.lender || '',
      leadSource: loan.leadSource || '',
      processor: loan.processor || '',
      state: loan.state || '',
      loanType: loan.loanType || '',
      isSelfGenerated: loan.isSelfGenerated,
      status: loan.status === 'Pending' ? 'In Process' : loan.status,
      submissionDate: loan.submissionDate || '',
      date: loan.date,
      fundingDate: loan.fundingDate || '',
      reimbursement: (loan.reimbursement || 0).toString(),
      cure: (loan.cure || 0).toString(),
      notes: loan.notes || '',
      referralSourceType: loan.referralSourceType || '',
      referrerName: loan.referrerName || '',
      referrerPhone: loan.referrerPhone || '',
    });
    setIsDialogOpen(true);
  };

  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({ open: false, loanId: null, clientName: '' });

  const handleDeleteLoan = async (id) => {
    try {
      await axios.delete(`${API}/api/loans/${id}`);
      setLoans(loans.filter(loan => loan.id !== id));
      setDeleteConfirmDialog({ open: false, loanId: null, clientName: '' });
    } catch (error) {
      console.error('Error deleting loan:', error);
      alert(error.response?.data?.detail || 'Error deleting loan. Please try again.');
    }
  };

  const [statusPickerDialog, setStatusPickerDialog] = useState({ open: false, loanId: null, currentStatus: '' });
  const [dateEditDialog, setDateEditDialog] = useState({ open: false, loanId: null, field: '', value: '', label: '' });

  const handleStatusClick = (loan) => {
    setStatusPickerDialog({ open: true, loanId: loan.id, currentStatus: loan.status || 'In Process' });
  };

  const handleStatusSelect = async (newStatus) => {
    const { loanId, currentStatus } = statusPickerDialog;
    if (newStatus === currentStatus) {
      setStatusPickerDialog({ open: false, loanId: null, currentStatus: '' });
      return;
    }

    // If changing to Closed, show funding date dialog
    if (newStatus === 'Closed') {
      const loan = loans.find(l => l.id === loanId);
      let defaultFundingDate = loan?.fundingDate;
      if (!defaultFundingDate && loan?.date) {
        const [y, m, d] = loan.date.split('-').map(Number);
        const fundDate = new Date(y, m - 1, d + 3);
        defaultFundingDate = `${fundDate.getFullYear()}-${String(fundDate.getMonth() + 1).padStart(2, '0')}-${String(fundDate.getDate()).padStart(2, '0')}`;
      }
      setStatusPickerDialog({ open: false, loanId: null, currentStatus: '' });
      setFundingDateDialog({ 
        open: true, 
        loanId, 
        fundingDate: defaultFundingDate || new Date().toISOString().split('T')[0],
        closingDate: loan?.date || new Date().toISOString().split('T')[0]
      });
      return;
    }

    try {
      await axios.put(`${API}/api/loans/${loanId}`, { status: newStatus });
      setLoans(loans.map(l => l.id === loanId ? { ...l, status: newStatus } : l));
      setStatusPickerDialog({ open: false, loanId: null, currentStatus: '' });
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating loan status. Please try again.');
    }
  };

  const handleFundingDateSubmit = async () => {
    const { loanId, fundingDate, closingDate } = fundingDateDialog;
    if (!closingDate || !fundingDate) {
      alert('Please enter both the Closing Date and Funding Date to proceed.');
      return;
    }
    try {
      await axios.put(`${API}/api/loans/${loanId}`, { 
        status: 'Closed',
        funding_date: fundingDate,
        date: closingDate
      });
      setLoans(loans.map(l => l.id === loanId ? { ...l, status: 'Closed', fundingDate: fundingDate, date: closingDate } : l));
      setFundingDateDialog({ open: false, loanId: null, fundingDate: '', closingDate: '' });
    } catch (error) {
      console.error('Error updating loan:', error);
      alert('Error updating loan. Please try again.');
    }
  };

  const handleDateEdit = (loan, field, label) => {
    const currentValue = field === 'date' ? (loan.date || '') : (loan.fundingDate || '');
    setDateEditDialog({ open: true, loanId: loan.id, field, value: currentValue, label });
  };

  const handleDateEditSubmit = async () => {
    const { loanId, field, value } = dateEditDialog;
    if (!value) {
      alert('Please enter a date.');
      return;
    }
    try {
      const updatePayload = field === 'date' ? { date: value } : { funding_date: value };
      await axios.put(`${API}/api/loans/${loanId}`, updatePayload);
      setLoans(loans.map(l => {
        if (l.id !== loanId) return l;
        return field === 'date' ? { ...l, date: value } : { ...l, fundingDate: value };
      }));
      setDateEditDialog({ open: false, loanId: null, field: '', value: '', label: '' });
    } catch (error) {
      console.error('Error updating date:', error);
      alert('Error updating date. Please try again.');
    }
  };

  // Generate PDF Report
  const generatePDF = () => {
    try {
      const doc = new jsPDF();
      const brokerTypeConfig = getCommissionTiers(effectiveBrokerType);
      const monthName = formatMonth(selectedMonth);
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 15;
      const contentWidth = pageWidth - margin * 2;

      // Helper functions
      const fmt = (n) => '$' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const drawLine = (y, color = [200, 200, 200]) => {
        doc.setDrawColor(...color);
        doc.setLineWidth(0.3);
        doc.line(margin, y, pageWidth - margin, y);
      };

      // ── HEADER ──
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(100, 100, 100);
      doc.text('KINFOLK HOME LOANS', margin, 18);
      
      doc.setFontSize(18);
      doc.setTextColor(0, 0, 0);
      doc.text('Commission Report', margin, 28);
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(80, 80, 80);
      doc.text(monthName, margin, 35);

      // Right-aligned broker info
      doc.setFontSize(9);
      doc.text(`Broker: ${brokerTypeConfig.name}`, pageWidth - margin, 20, { align: 'right' });
      doc.text(`Tier: ${monthTier.name} (${(monthTier.rate * 100).toFixed(0)}%)`, pageWidth - margin, 26, { align: 'right' });
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth - margin, 32, { align: 'right' });

      drawLine(39, [0, 0, 0]);

      // ── SUMMARY ──
      let y = 48;
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.setFont(undefined, 'bold');
      doc.text('SUMMARY', margin, y);
      y += 8;

      // Summary grid
      doc.setFont(undefined, 'normal');
      doc.setTextColor(60, 60, 60);
      const summaryItems = [
        { label: 'Total Loans', value: String(monthlyStats.totalLoans) },
        { label: 'Closed', value: String(monthlyStats.closedLoans) },
      ];
      
      const colWidth = contentWidth / 3;
      summaryItems.forEach((item, i) => {
        const x = margin + i * colWidth;
        doc.setFontSize(8);
        doc.setTextColor(130, 130, 130);
        doc.text(item.label, x, y);
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.setFont(undefined, 'bold');
        doc.text(item.value, x, y + 7);
        doc.setFont(undefined, 'normal');
      });

      // Total commission - highlighted
      const commX = margin + 2 * colWidth;
      doc.setFontSize(8);
      doc.setTextColor(130, 130, 130);
      doc.text('Total Commission', commX, y);
      doc.setFontSize(14);
      doc.setTextColor(16, 130, 80);
      doc.setFont(undefined, 'bold');
      doc.text(fmt(monthlyStats.totalCommission), commX, y + 7);
      doc.setFont(undefined, 'normal');
      
      y += 16;
      drawLine(y);

      // ── LOAN TABLE ──
      y += 10;
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.setFont(undefined, 'bold');
      doc.text('LOAN BREAKDOWN', margin, y);
      y += 8;

      // Table column definitions - clean 6-column layout
      const rightEdge = pageWidth - margin;
      const cols = [
        { label: '#',           x: margin,      align: 'left' },
        { label: 'Client Name', x: margin + 10, align: 'left' },
        { label: 'Loan #',     x: margin + 50, align: 'left' },
        { label: 'Lender',     x: margin + 85, align: 'left' },
        { label: 'Amount',     x: margin + 120, align: 'right' },
        { label: 'Rate',       x: margin + 140, align: 'center' },
        { label: 'Commission', x: rightEdge,   align: 'right' },
      ];

      // Table header
      doc.setFillColor(245, 245, 245);
      doc.rect(margin, y - 3.5, contentWidth, 7, 'F');
      doc.setFontSize(7.5);
      doc.setTextColor(100, 100, 100);
      doc.setFont(undefined, 'bold');
      cols.forEach(col => {
        doc.text(col.label, col.x, y, { align: col.align === 'right' ? 'right' : col.align === 'center' ? 'center' : undefined });
      });

      y += 6;
      drawLine(y - 2);

      // Table rows
      let grandTotalBase = 0;
      let grandTotalAdj = 0;
      let grandTotalNet = 0;
      let grandTotalAmount = 0;

      closedMonthLoans.forEach((loan, index) => {
        // Page break check (extra space for possible adjustment sub-line)
        if (y > 265) {
          doc.addPage();
          y = 20;
          // Repeat header on new page
          doc.setFillColor(245, 245, 245);
          doc.rect(margin, y - 3.5, contentWidth, 7, 'F');
          doc.setFontSize(7.5);
          doc.setTextColor(100, 100, 100);
          doc.setFont(undefined, 'bold');
          cols.forEach(col => {
            doc.text(col.label, col.x, y, { align: col.align === 'right' ? 'right' : col.align === 'center' ? 'center' : undefined });
          });
          y += 6;
          drawLine(y - 2);
        }

        const baseCommission = calculateLoanCommission(loan, closedMonthLoans.length, effectiveBrokerType);
        const reimbursement = parseFloat(loan.reimbursement || 0);
        const cure = parseFloat(loan.cure || 0);
        const adjustment = reimbursement - cure;
        const netCommission = baseCommission + adjustment;

        grandTotalBase += baseCommission;
        grandTotalAdj += adjustment;
        grandTotalNet += netCommission;
        grandTotalAmount += (loan.loanAmount || 0);

        // Alternating row background
        if (index % 2 === 0) {
          doc.setFillColor(252, 252, 252);
          doc.rect(margin, y - 3, contentWidth, adjustment !== 0 ? 13 : 8, 'F');
        }

        doc.setFontSize(8);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(40, 40, 40);

        // Main row
        doc.text(String(index + 1), cols[0].x, y);
        doc.setFont(undefined, 'bold');
        const clientLabel = loan.isSelfGenerated ? `${(loan.clientName || 'Unknown').substring(0, 24)} *` : (loan.clientName || 'Unknown').substring(0, 26);
        doc.text(clientLabel, cols[1].x, y);
        doc.setFont(undefined, 'normal');
        doc.text((loan.loanNumber || '—').substring(0, 18), cols[2].x, y);
        const lenderAbbrev = {'United Wholesale Mortgage': 'UWM'};
        const lenderDisplay = lenderAbbrev[loan.lender] || loan.lender || '—';
        doc.text(lenderDisplay.substring(0, 18), cols[3].x, y);
        doc.text('$' + (loan.loanAmount || 0).toLocaleString(), cols[4].x, y, { align: 'right' });
        
        const selfGenRateText = `${(getCommissionTiers(effectiveBrokerType).selfGenRate * 100).toFixed(0)}%`;
        const rateText = loan.isSelfGenerated ? selfGenRateText : `${(monthTier.rate * 100).toFixed(0)}%`;
        doc.text(rateText, cols[5].x, y, { align: 'center' });
        
        doc.setFont(undefined, 'bold');
        doc.text(fmt(netCommission), rightEdge, y, { align: 'right' });
        doc.setFont(undefined, 'normal');

        y += 8;
      });

      // ── TOTALS ROW ──
      drawLine(y - 2, [0, 0, 0]);
      y += 3;
      doc.setFillColor(240, 240, 240);
      doc.rect(margin, y - 3.5, contentWidth, 8, 'F');
      doc.setFontSize(8);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text('TOTALS', cols[1].x, y);
      doc.text(closedMonthLoans.length + ' loans', cols[2].x, y);
      doc.text('$' + grandTotalAmount.toLocaleString(), cols[4].x, y, { align: 'right' });
      doc.setTextColor(16, 130, 80);
      doc.setFontSize(9);
      doc.text(fmt(grandTotalNet), rightEdge, y, { align: 'right' });

      // Self-Gen legend
      y += 10;
      doc.setFontSize(7);
      doc.setFont(undefined, 'italic');
      doc.setTextColor(100, 100, 100);
      doc.text(`* Self-Generated lead (${(getCommissionTiers(effectiveBrokerType).selfGenRate * 100).toFixed(0)}% commission rate)`, margin, y);

      // ── ADJUSTMENTS SECTION ──
      const loansWithAdj = closedMonthLoans
        .map((loan, idx) => {
          const base = calculateLoanCommission(loan, closedMonthLoans.length, effectiveBrokerType);
          const reimb = parseFloat(loan.reimbursement || 0);
          const cure = parseFloat(loan.cure || 0);
          if (reimb === 0 && cure === 0) return null;
          return { ...loan, rowNum: idx + 1, base, reimb, cure, net: base + reimb - cure };
        })
        .filter(Boolean);

      if (loansWithAdj.length > 0) {
        y += 14;
        if (y > 250) { doc.addPage(); y = 20; }

        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.setFont(undefined, 'bold');
        doc.text('ADJUSTMENTS', margin, y);
        y += 8;

        // Adjustment table header
        doc.setFillColor(245, 245, 245);
        doc.rect(margin, y - 3.5, contentWidth, 7, 'F');
        doc.setFontSize(7.5);
        doc.setTextColor(100, 100, 100);
        doc.setFont(undefined, 'bold');
        doc.text('#', margin, y);
        doc.text('Client Name', margin + 10, y);
        doc.text('Base Comm', margin + 65, y);
        doc.text('Reimbursement', margin + 100, y);
        doc.text('Cure', margin + 140, y);
        doc.text('Net Commission', rightEdge, y, { align: 'right' });
        y += 6;
        drawLine(y - 2);

        let totalReimb = 0;
        let totalCure = 0;

        loansWithAdj.forEach((loan, i) => {
          if (y > 270) { doc.addPage(); y = 20; }
          totalReimb += loan.reimb;
          totalCure += loan.cure;

          if (i % 2 === 0) {
            doc.setFillColor(252, 252, 252);
            doc.rect(margin, y - 3, contentWidth, 8, 'F');
          }

          doc.setFontSize(8);
          doc.setFont(undefined, 'normal');
          doc.setTextColor(40, 40, 40);
          doc.text(String(loan.rowNum), margin, y);
          doc.setFont(undefined, 'bold');
          doc.text((loan.clientName || 'Unknown').substring(0, 28), margin + 10, y);
          doc.setFont(undefined, 'normal');
          doc.text(fmt(loan.base), margin + 65, y);

          if (loan.reimb > 0) {
            doc.setTextColor(16, 130, 80);
            doc.text('+' + fmt(loan.reimb), margin + 100, y);
          } else {
            doc.setTextColor(160, 160, 160);
            doc.text('—', margin + 100, y);
          }

          if (loan.cure > 0) {
            doc.setTextColor(200, 50, 50);
            doc.text('-' + fmt(loan.cure), margin + 140, y);
          } else {
            doc.setTextColor(160, 160, 160);
            doc.text('—', margin + 140, y);
          }

          doc.setTextColor(0, 0, 0);
          doc.setFont(undefined, 'bold');
          doc.text(fmt(loan.net), rightEdge, y, { align: 'right' });
          doc.setFont(undefined, 'normal');
          y += 8;
        });

        // Adjustment totals row
        drawLine(y - 2, [0, 0, 0]);
        y += 3;
        doc.setFillColor(240, 240, 240);
        doc.rect(margin, y - 3.5, contentWidth, 8, 'F');
        doc.setFontSize(8);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('ADJ TOTALS', margin + 10, y);
        if (totalReimb > 0) {
          doc.setTextColor(16, 130, 80);
          doc.text('+' + fmt(totalReimb), margin + 100, y);
        }
        if (totalCure > 0) {
          doc.setTextColor(200, 50, 50);
          doc.text('-' + fmt(totalCure), margin + 140, y);
        }
        doc.setTextColor(0, 0, 0);
        doc.text(fmt(totalReimb - totalCure), rightEdge, y, { align: 'right' });
      }

      // ── FOOTER ──
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(150, 150, 150);
        drawLine(283, [200, 200, 200]);
        doc.text('Kinfolk Home Loans - Commission Report', margin, 288);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, 288, { align: 'right' });
      }

      doc.save(`Commission_Report_${monthName.replace(' ', '_')}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Admin/Processor Loan Officer Selector */}
      {(isAdmin || isProcessor) && (
        <Card className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 border-blue-800/50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-blue-400" />
                <span className="text-white font-medium">Viewing:</span>
                <button
                  onClick={() => setShowOfficerDropdown(true)}
                  data-testid="lm-officer-dropdown-trigger"
                  className="bg-[#1a1a1a] border border-[#333] rounded-lg px-4 py-2 text-white hover:border-blue-500 focus:border-blue-500 focus:outline-none min-w-[220px] flex items-center justify-between transition-colors"
                >
                  <span>
                    {selectedOfficer === 'mine' ? 'My Loans Only' : 
                     selectedOfficer === 'all' ? 'Company Wide (Sales)' :
                     selectedOfficer === 'all_processing' ? 'Company Wide (Processing)' :
                     loanOfficers.find(o => o.id === selectedOfficer)?.name || 'Select'}
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-400" />
                </button>
              </div>
              {selectedOfficer === 'all' && (
                <Badge className="bg-purple-500 text-white">Company Wide (Sales)</Badge>
              )}
              {selectedOfficer === 'all_processing' && (
                <Badge className="bg-teal-500 text-white">Company Wide (Processing)</Badge>
              )}
              {selectedOfficer !== 'mine' && selectedOfficer !== 'all' && selectedOfficer !== 'all_processing' && viewingProcessor && (
                <Badge className="bg-teal-500 text-white">
                  Viewing Processor: {loanOfficers.find(o => o.id === selectedOfficer)?.name}
                </Badge>
              )}
              {selectedOfficer !== 'mine' && selectedOfficer !== 'all' && selectedOfficer !== 'all_processing' && !viewingProcessor && (
                <Badge className="bg-purple-500 text-white">
                  Viewing: {loanOfficers.find(o => o.id === selectedOfficer)?.name}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Officer Selection Dialog */}
      <Dialog open={showOfficerDropdown} onOpenChange={setShowOfficerDropdown}>
        <DialogContent className="bg-[#111] border-[#222] text-white max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-white text-base">Select Team Member</DialogTitle>
            <DialogDescription className="text-gray-400 text-xs">Choose a loan officer or processor to view their data</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-1 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-3 gap-2 mb-1">
              {!isProcessor && (
                <button
                  onClick={() => { setSelectedOfficer('mine'); setShowOfficerDropdown(false); }}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center gap-2 ${
                    selectedOfficer === 'mine' ? 'bg-blue-900/40 text-blue-400 border border-blue-700 ring-2 ring-blue-500/20' : 'bg-[#1a1a1a] border border-[#333] text-gray-300 hover:border-blue-600 hover:bg-[#1e1e1e]'
                  }`}
                >
                  <div className="w-7 h-7 rounded-full bg-blue-900/50 flex items-center justify-center flex-shrink-0">
                    <Users className="w-3.5 h-3.5 text-blue-400" />
                  </div>
                  My Loans Only
                </button>
              )}
              <button
                onClick={() => { setSelectedOfficer('all'); setShowOfficerDropdown(false); }}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center gap-2 ${
                  selectedOfficer === 'all' ? 'bg-purple-900/40 text-purple-400 border border-purple-700 ring-2 ring-purple-500/20' : 'bg-[#1a1a1a] border border-[#333] text-gray-300 hover:border-purple-600 hover:bg-[#1e1e1e]'
                }`}
              >
                <div className="w-7 h-7 rounded-full bg-purple-900/50 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-3.5 h-3.5 text-purple-400" />
                </div>
                Company Wide (Sales)
              </button>
              <button
                onClick={() => { setSelectedOfficer('all_processing'); setShowOfficerDropdown(false); }}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-all flex items-center gap-2 ${
                  selectedOfficer === 'all_processing' ? 'bg-teal-900/40 text-teal-400 border border-teal-700 ring-2 ring-teal-500/20' : 'bg-[#1a1a1a] border border-[#333] text-gray-300 hover:border-teal-600 hover:bg-[#1e1e1e]'
                }`}
              >
                <div className="w-7 h-7 rounded-full bg-teal-900/50 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-3.5 h-3.5 text-teal-400" />
                </div>
                Company Wide (Processing)
              </button>
            </div>
            {officersList.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-xs text-green-400 uppercase tracking-widest font-bold">Loan Officers</span>
                  <div className="flex-1 h-px bg-green-900/50 ml-1" />
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                  {officersList.map(officer => (
                    <button
                      key={officer.id}
                      onClick={() => { setSelectedOfficer(officer.id); setShowOfficerDropdown(false); }}
                      className={`px-2 py-2 rounded-md text-xs transition-all flex items-center gap-2 ${
                        selectedOfficer === officer.id ? 'bg-green-900/30 text-green-400 border border-green-800 ring-1 ring-green-500/20' : 'bg-[#1a1a1a] border border-[#333] text-gray-300 hover:border-green-700 hover:bg-[#1e1e1e]'
                      }`}
                    >
                      <div className="w-7 h-7 rounded-full bg-green-900/40 flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-green-400">
                        {officer.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <span className="truncate font-medium">{officer.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {processorsList.length > 0 && (
              <div>
                {officersList.length > 0 && <div className="h-px bg-[#333] my-1" />}
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-teal-500" />
                  <span className="text-xs text-teal-400 uppercase tracking-widest font-bold">Processors</span>
                  <div className="flex-1 h-px bg-teal-900/50 ml-1" />
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                  {processorsList.map(processor => (
                    <button
                      key={processor.id}
                      onClick={() => { setSelectedOfficer(processor.id); setShowOfficerDropdown(false); }}
                      className={`px-2 py-2 rounded-md text-xs transition-all flex items-center gap-2 ${
                        selectedOfficer === processor.id ? 'bg-teal-900/30 text-teal-400 border border-teal-800 ring-1 ring-teal-500/20' : 'bg-[#1a1a1a] border border-[#333] text-gray-300 hover:border-teal-700 hover:bg-[#1e1e1e]'
                      }`}
                    >
                      <div className="w-7 h-7 rounded-full bg-teal-900/40 flex items-center justify-center flex-shrink-0 text-[10px] font-bold text-teal-400">
                        {processor.name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <span className="truncate font-medium">{processor.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <h1 className="text-2xl lg:text-3xl font-semibold text-white">
            Loan Management
          </h1>
          <p className="text-gray-400 text-sm">
            {(isProcessor || viewingProcessor) ? 'View and manage all loan officer loans' : 'Track and manage your monthly loan closings'}
          </p>
        </div>
        
        {(isProcessor || viewingProcessor) ? (
          <Badge className="bg-teal-900/50 text-teal-400 border border-teal-700 text-sm px-3 py-1.5">
            Processor View — All Loans
          </Badge>
        ) : (
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setEditingLoan(null);
            setFormErrors({});
            // Use selected month for the default date
            const defaultDate = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 15);
            setFormData({
              clientName: '',
              loanNumber: '',
              loanAmount: '',
              baseCommissionRate: '2.75',
              commissionType: 'percent',
              lender: '',
              leadSource: '',
              processor: '',
              state: '',
              loanType: '',
              isSelfGenerated: false,
              status: '',
              submissionDate: '',
              date: defaultDate.toISOString().split('T')[0],
              fundingDate: '',
              reimbursement: '0',
              cure: '0',
              notes: '',
            });
          }
        }}>
          <DialogTrigger asChild>
            <Button 
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={() => {
                // Set default date to middle of selected month when opening dialog
                const defaultDate = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 15);
                setFormData(prev => ({
                  ...prev,
                  date: defaultDate.toISOString().split('T')[0]
                }));
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add New Loan
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#111] border-[#222] text-white max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">{editingLoan ? 'Edit Loan' : 'Add New Loan'}</DialogTitle>
              <DialogDescription className="text-gray-400">
                {editingLoan ? 'Update the details of your loan closing' : 'Enter the details of your new loan closing'}
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2.5 py-3">
              {/* Left Column */}
              <div className="space-y-2">
                <Label htmlFor="clientName" className="text-gray-300">Client Name</Label>
                <Input
                  id="clientName"
                  placeholder="John Smith"
                  value={formData.clientName}
                  onChange={(e) => {
                    setFormData({ ...formData, clientName: e.target.value });
                    if (formErrors.clientName) setFormErrors({ ...formErrors, clientName: '' });
                  }}
                  className={`bg-[#1a1a1a] border-[#333] text-white focus:border-green-500 ${formErrors.clientName ? 'border-red-500' : ''}`}
                />
                {formErrors.clientName && <p className="text-xs text-red-400">{formErrors.clientName}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="loanNumber" className="text-gray-300">Loan Number</Label>
                <Input
                  id="loanNumber"
                  placeholder="e.g. LN-2025-001234"
                  value={formData.loanNumber}
                  onChange={(e) => {
                    setFormData({ ...formData, loanNumber: e.target.value });
                    if (formErrors.loanNumber) setFormErrors({ ...formErrors, loanNumber: '' });
                  }}
                  className={`bg-[#1a1a1a] border-[#333] text-white focus:border-green-500 ${formErrors.loanNumber ? 'border-red-500' : ''}`}
                />
                {formErrors.loanNumber && <p className="text-xs text-red-400">{formErrors.loanNumber}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="loanAmount" className="text-gray-300">Loan Amount ($)</Label>
                <Input
                  id="loanAmount"
                  type="text"
                  placeholder="450,000"
                  value={formatNumberWithCommas(formData.loanAmount)}
                  onChange={(e) => {
                    handleNumberChange('loanAmount', e.target.value);
                    if (formErrors.loanAmount) setFormErrors({ ...formErrors, loanAmount: '' });
                  }}
                  className={`bg-[#1a1a1a] border-[#333] text-white focus:border-green-500 ${formErrors.loanAmount ? 'border-red-500' : ''}`}
                />
                {formErrors.loanAmount && <p className="text-xs text-red-400">{formErrors.loanAmount}</p>}
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Lender</Label>
                <button
                  type="button"
                  onClick={() => setPickerOpen('lender')}
                  className={`w-full px-3 py-2 bg-[#1a1a1a] border rounded-md text-left text-sm transition-colors hover:border-green-600 ${formErrors.lender ? 'border-red-500' : 'border-[#333]'} ${formData.lender ? 'text-white' : 'text-gray-500'}`}
                  data-testid="lender-picker-btn"
                >
                  {formData.lender || 'Select Lender'}
                </button>
                {formErrors.lender && <p className="text-xs text-red-400">{formErrors.lender}</p>}
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Lead Source</Label>
                <button
                  type="button"
                  onClick={() => setPickerOpen('leadSource')}
                  className={`w-full px-3 py-2 bg-[#1a1a1a] border rounded-md text-left text-sm transition-colors hover:border-green-600 ${formErrors.leadSource ? 'border-red-500' : 'border-[#333]'} ${formData.leadSource ? 'text-white' : 'text-gray-500'}`}
                  data-testid="leadsource-picker-btn"
                >
                  {formData.leadSource || 'Select Lead Source'}
                </button>
                {formErrors.leadSource && <p className="text-xs text-red-400">{formErrors.leadSource}</p>}
              </div>

              {/* Referral Information - conditionally shown */}
              {formData.leadSource === 'Referral' && (
                <div className="col-span-2 border border-[#333] rounded-lg p-4 space-y-3 bg-[#0d0d0d]">
                  <h3 className="text-sm font-semibold text-amber-400 flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                    Referral Information
                  </h3>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-gray-300 text-xs">Source Type</Label>
                      <button
                        type="button"
                        onClick={() => setPickerOpen('referralSourceType')}
                        className={`w-full px-3 py-2 bg-[#1a1a1a] border rounded-md text-left text-sm transition-colors hover:border-amber-600 ${formErrors.referralSourceType ? 'border-red-500' : 'border-[#333]'} ${formData.referralSourceType ? 'text-white' : 'text-gray-500'}`}
                        data-testid="referral-source-type-btn"
                      >
                        {formData.referralSourceType || 'Select Type'}
                      </button>
                      {formErrors.referralSourceType && <p className="text-xs text-red-400">{formErrors.referralSourceType}</p>}
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-gray-300 text-xs">Referrer's Name</Label>
                      <Input
                        value={formData.referrerName}
                        onChange={(e) => {
                          setFormData({ ...formData, referrerName: e.target.value });
                          if (formErrors.referrerName) setFormErrors({ ...formErrors, referrerName: '' });
                        }}
                        placeholder="Full name"
                        className={`bg-[#1a1a1a] border-[#333] text-white focus:border-amber-500 ${formErrors.referrerName ? 'border-red-500' : ''}`}
                        data-testid="referrer-name-input"
                      />
                      {formErrors.referrerName && <p className="text-xs text-red-400">{formErrors.referrerName}</p>}
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-gray-300 text-xs">Referrer's Phone</Label>
                      <Input
                        value={formData.referrerPhone}
                        onChange={(e) => {
                          setFormData({ ...formData, referrerPhone: e.target.value });
                          if (formErrors.referrerPhone) setFormErrors({ ...formErrors, referrerPhone: '' });
                        }}
                        placeholder="(555) 555-5555"
                        className={`bg-[#1a1a1a] border-[#333] text-white focus:border-amber-500 ${formErrors.referrerPhone ? 'border-red-500' : ''}`}
                        data-testid="referrer-phone-input"
                      />
                      {formErrors.referrerPhone && <p className="text-xs text-red-400">{formErrors.referrerPhone}</p>}
                    </div>
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-gray-300">Processor</Label>
                <button
                  type="button"
                  onClick={() => setPickerOpen('processor')}
                  className={`w-full px-3 py-2 bg-[#1a1a1a] border rounded-md text-left text-sm transition-colors hover:border-green-600 ${formErrors.processor ? 'border-red-500' : 'border-[#333]'} ${formData.processor ? 'text-white' : 'text-gray-500'}`}
                  data-testid="processor-picker-btn"
                >
                  {formData.processor || 'Select Processor'}
                </button>
                {formErrors.processor && <p className="text-xs text-red-400">{formErrors.processor}</p>}
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">State</Label>
                <button
                  type="button"
                  onClick={() => setPickerOpen('state')}
                  className={`w-full px-3 py-2 bg-[#1a1a1a] border rounded-md text-left text-sm transition-colors hover:border-green-600 ${formErrors.state ? 'border-red-500' : 'border-[#333]'} ${formData.state ? 'text-white' : 'text-gray-500'}`}
                  data-testid="state-picker-btn"
                >
                  {formData.state || 'Select State'}
                </button>
                {formErrors.state && <p className="text-xs text-red-400">{formErrors.state}</p>}
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Loan Type</Label>
                <button
                  type="button"
                  onClick={() => setPickerOpen('loanType')}
                  className={`w-full px-3 py-2 bg-[#1a1a1a] border rounded-md text-left text-sm transition-colors hover:border-green-600 ${formErrors.loanType ? 'border-red-500' : 'border-[#333]'} ${formData.loanType ? 'text-white' : 'text-gray-500'}`}
                  data-testid="loantype-picker-btn"
                >
                  {formData.loanType || 'Select Loan Type'}
                </button>
                {formErrors.loanType && <p className="text-xs text-red-400">{formErrors.loanType}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="baseRate" className="text-gray-300">Base Commission</Label>
                <div className="flex gap-2">
                  <div className="flex rounded-lg overflow-hidden border border-[#333] flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({ ...formData, commissionType: 'percent', baseCommissionRate: formData.commissionType === 'flat' ? '2.75' : formData.baseCommissionRate });
                      }}
                      className={`px-3 py-2 text-sm font-medium transition-colors ${
                        formData.commissionType === 'percent'
                          ? 'bg-green-600 text-white'
                          : 'bg-[#1a1a1a] text-gray-400 hover:text-white'
                      }`}
                      data-testid="commission-type-percent"
                    >
                      %
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({ ...formData, commissionType: 'flat', baseCommissionRate: formData.commissionType === 'percent' ? '' : formData.baseCommissionRate });
                      }}
                      className={`px-3 py-2 text-sm font-medium transition-colors ${
                        formData.commissionType === 'flat'
                          ? 'bg-green-600 text-white'
                          : 'bg-[#1a1a1a] text-gray-400 hover:text-white'
                      }`}
                      data-testid="commission-type-flat"
                    >
                      $
                    </button>
                  </div>
                  <Input
                    id="baseRate"
                    type="text"
                    placeholder={formData.commissionType === 'percent' ? '2.75' : '5000'}
                    value={formData.baseCommissionRate}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^\d.]/g, '');
                      setFormData({ ...formData, baseCommissionRate: val });
                      if (formErrors.baseCommissionRate) setFormErrors({ ...formErrors, baseCommissionRate: '' });
                    }}
                    className={`bg-[#1a1a1a] border-[#333] text-white focus:border-green-500 ${formErrors.baseCommissionRate ? 'border-red-500' : ''}`}
                  />
                </div>
                {formErrors.baseCommissionRate && <p className="text-xs text-red-400">{formErrors.baseCommissionRate}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="status" className="text-gray-300">Status</Label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) => {
                    setFormData({ ...formData, status: e.target.value });
                    if (formErrors.status) setFormErrors({ ...formErrors, status: '' });
                  }}
                  className={`w-full px-3 py-2 bg-[#1a1a1a] border rounded-md text-white focus:border-green-500 focus:outline-none ${formErrors.status ? 'border-red-500' : 'border-[#333]'}`}
                >
                  <option value="">Select Status</option>
                  <option value="In Process">In Process</option>
                  <option value="Closed">Closed</option>
                  <option value="Withdrawn">Withdrawn</option>
                </select>
                {formErrors.status && <p className="text-xs text-red-400">{formErrors.status}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="submissionDate" className="text-gray-300">Submission Date</Label>
                <Input
                  id="submissionDate"
                  type="date"
                  value={formData.submissionDate}
                  onChange={(e) => {
                    const newSubmissionDate = e.target.value;
                    setFormData({ 
                      ...formData, 
                      submissionDate: newSubmissionDate,
                      status: newSubmissionDate ? 'In Process' : formData.status
                    });
                    if (formErrors.submissionDate) setFormErrors({ ...formErrors, submissionDate: '' });
                  }}
                  className={`bg-[#1a1a1a] border-[#333] text-white focus:border-green-500 ${formErrors.submissionDate ? 'border-red-500' : ''}`}
                />
                {formErrors.submissionDate && <p className="text-xs text-red-400">{formErrors.submissionDate}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="date" className="text-gray-300">Closing Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => {
                    setFormData({ ...formData, date: e.target.value });
                    if (formErrors.date) setFormErrors({ ...formErrors, date: '' });
                  }}
                  className={`bg-[#1a1a1a] border-[#333] text-white focus:border-green-500 ${formErrors.date ? 'border-red-500' : ''}`}
                />
                {formErrors.date && <p className="text-xs text-red-400">{formErrors.date}</p>}
              </div>
              <div className="space-y-2">
                <Label htmlFor="reimbursement" className="text-gray-300">Reimbursement ($)</Label>
                <Input
                  id="reimbursement"
                  type="text"
                  placeholder="0"
                  value={formatNumberWithCommas(formData.reimbursement)}
                  onChange={(e) => handleNumberChange('reimbursement', e.target.value)}
                  className="bg-[#1a1a1a] border-[#333] text-white focus:border-green-500"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cure" className="text-gray-300">Cure ($)</Label>
                <Input
                  id="cure"
                  type="text"
                  placeholder="0"
                  value={formatNumberWithCommas(formData.cure)}
                  onChange={(e) => handleNumberChange('cure', e.target.value)}
                  className="bg-[#1a1a1a] border-[#333] text-white focus:border-green-500"
                />
              </div>

              {/* Full-width bottom section */}
              <div className="col-span-2 flex items-center space-x-2 pt-1">
                <input
                  type="checkbox"
                  id="selfGenerated"
                  checked={formData.isSelfGenerated}
                  onChange={(e) => setFormData({ ...formData, isSelfGenerated: e.target.checked })}
                  className="w-4 h-4 rounded border-[#333] bg-[#1a1a1a] text-green-600 focus:ring-green-500"
                />
                <Label htmlFor="selfGenerated" className="text-gray-300 cursor-pointer">
                  Self-Generated (70% commission rate)
                </Label>
              </div>
              <div className="col-span-2 space-y-1">
                <Label htmlFor="notes" className="text-gray-300">Additional Notes</Label>
                <textarea
                  id="notes"
                  placeholder="Enter any additional notes..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={1}
                  className="w-full px-3 py-2 bg-[#1a1a1a] border border-[#333] rounded-md text-white focus:border-green-500 focus:outline-none resize-none"
                />
              </div>
            </div>
            <div className="flex space-x-3">
              <Button
                onClick={handleAddLoan}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                {editingLoan ? 'Update Loan' : 'Add Loan'}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setIsDialogOpen(false);
                  setEditingLoan(null);
                }}
                className="border-[#333] text-gray-300 hover:bg-[#1a1a1a]"
              >
                Cancel
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Month / YTD Navigation */}
      <Card className="bg-[#111] border-[#222]">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            {lmViewMode === 'month' ? (
              <>
                <Button variant="outline" size="icon" onClick={() => navigateMonth(-1)} className="border-[#333] bg-[#1a1a1a] hover:bg-[#222] text-gray-300">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="text-center">
                  <div className="text-3xl font-bold text-white">{formatMonth(selectedMonth)}</div>
                  <div className="text-sm text-gray-500 mt-1">Navigate months with arrows</div>
                </div>
                <Button variant="outline" size="icon" onClick={() => navigateMonth(1)} className="border-[#333] bg-[#1a1a1a] hover:bg-[#222] text-gray-300">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" size="icon" onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear() - 1, selectedMonth.getMonth(), 1))} className="border-[#333] bg-[#1a1a1a] hover:bg-[#222] text-gray-300">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="text-center">
                  <div className="text-3xl font-bold text-white">{selectedMonth.getFullYear()}</div>
                  <div className="text-sm text-gray-500 mt-1">Jan – Dec {selectedMonth.getFullYear()}</div>
                </div>
                <Button variant="outline" size="icon" onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear() + 1, selectedMonth.getMonth(), 1))} className="border-[#333] bg-[#1a1a1a] hover:bg-[#222] text-gray-300">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
          {/* Monthly / YTD Toggle */}
          <div className="flex justify-center mt-3">
            <div className="bg-[#0a0a0a] rounded-lg p-1 flex gap-1">
              <button
                onClick={() => setLmViewMode('month')}
                data-testid="lm-view-mode-month"
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                  lmViewMode === 'month' ? 'bg-green-600 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setLmViewMode('ytd')}
                data-testid="lm-view-mode-ytd"
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                  lmViewMode === 'ytd' ? 'bg-green-600 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                YTD
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Stats */}
      <div className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-[#111] border-[#222] border-l-4 border-l-white">
            <CardContent className="pt-6">
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-400">Total Loans</div>
                <div className="text-2xl font-bold text-white">{monthlyStats.totalLoans}</div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#111] border-[#222] border-l-4 border-l-green-500">
            <CardContent className="pt-6">
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-400">Closed</div>
                <div className="text-2xl font-bold text-green-400">{monthlyStats.closedLoans}</div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#111] border-[#222] border-l-4 border-l-amber-500">
            <CardContent className="pt-6">
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-400">In Process</div>
                <div className="text-2xl font-bold text-amber-400">{monthlyStats.inProcessLoans}</div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#111] border-[#222] border-l-4 border-l-red-500">
            <CardContent className="pt-6">
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-400">Withdrawn</div>
                <div className="text-2xl font-bold text-red-400">{monthlyStats.withdrawnLoans}</div>
              </div>
            </CardContent>
          </Card>
          {!(isProcessor || viewingProcessor) && (
          <Card className="bg-[#111] border-[#222] border-l-4 border-l-green-500">
            <CardContent className="pt-6">
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-400">Commission</div>
                <div className="text-2xl font-bold text-green-400">
                  ${monthlyStats.totalCommission.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <Badge className="bg-green-900/50 text-green-400 border border-green-800 text-xs">
                  {monthTier.name} ({(monthTier.rate * 100).toFixed(0)}%)
                </Badge>
              </div>
            </CardContent>
          </Card>
          )}
        </div>

        {/* Export PDF Button */}
        {!(isProcessor || viewingProcessor) && (
          <div className="flex justify-end">
            <Button
              onClick={generatePDF}
              className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-40 disabled:cursor-not-allowed"
              size="lg"
              disabled={monthLoans.length === 0}
            >
              <Download className="w-4 h-4 mr-2" />
              Export PDF Report for Accounting
            </Button>
          </div>
        )}
      </div>

      {/* Loans List */}
      <Card className="bg-[#111] border-[#222]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white">Loans for {lmViewMode === 'ytd' ? `${selectedMonth.getFullYear()} YTD` : formatMonth(selectedMonth)}</CardTitle>
              <CardDescription className="text-gray-400">
                {monthLoans.length === 0 
                  ? `No loans recorded for ${lmViewMode === 'ytd' ? selectedMonth.getFullYear() : formatMonth(selectedMonth)}` 
                  : `${monthLoans.length} loan${monthLoans.length > 1 ? 's' : ''} ${lmViewMode === 'ytd' ? 'this year' : 'this month'}`}
              </CardDescription>
            </div>
            <div className="flex items-center gap-3">
              {(isProcessor || viewingProcessor) && monthLoansRaw.length > 0 && (
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <Input
                  placeholder="Filter by LO, client, or processor..."
                  value={processorFilter}
                  onChange={(e) => setProcessorFilter(e.target.value)}
                  className="bg-[#1a1a1a] border-[#333] text-white pl-9 w-[260px] h-9 text-sm"
                  data-testid="processor-filter-input"
                />
              </div>
            )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {monthLoans.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 text-gray-700 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No loans for this month</p>
              {!(isProcessor || viewingProcessor) && (
              <Button
                onClick={() => {
                  // Set default date to middle of selected month
                  const defaultDate = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 15);
                  setFormData(prev => ({
                    ...prev,
                    date: defaultDate.toISOString().split('T')[0]
                  }));
                  setIsDialogOpen(true);
                }}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Loan
              </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#333]">
                    <th className="text-left py-3 px-3 text-gray-400 font-medium">
                      <button onClick={() => { setSortField('clientName'); setSortOrder(prev => sortField === 'clientName' ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'); }} className="inline-flex items-center gap-1 hover:text-white transition-colors" data-testid="sort-client">
                        Client {sortField === 'clientName' && (sortOrder === 'asc' ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />)}
                      </button>
                    </th>
                    <th className="text-left py-3 px-3 text-gray-400 font-medium">Loan #</th>
                    <th className="text-right py-3 px-3 text-gray-400 font-medium">
                      <button onClick={() => { setSortField('loanAmount'); setSortOrder(prev => sortField === 'loanAmount' ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'); }} className="inline-flex items-center gap-1 hover:text-white transition-colors ml-auto" data-testid="sort-amount">
                        Amount {sortField === 'loanAmount' && (sortOrder === 'asc' ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />)}
                      </button>
                    </th>
                    <th className="text-left py-3 px-3 text-gray-400 font-medium">
                      <button onClick={() => { setSortField('lender'); setSortOrder(prev => sortField === 'lender' ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'); }} className="inline-flex items-center gap-1 hover:text-white transition-colors" data-testid="sort-lender">
                        Lender {sortField === 'lender' && (sortOrder === 'asc' ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />)}
                      </button>
                    </th>
                    <th className="text-left py-3 px-3 text-gray-400 font-medium">
                      {(isProcessor || viewingProcessor) ? (
                        <button onClick={() => { setSortField('processor'); setSortOrder(prev => sortField === 'processor' ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'); }} className="inline-flex items-center gap-1 hover:text-white transition-colors" data-testid="sort-processor">
                          Processor {sortField === 'processor' && (sortOrder === 'asc' ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />)}
                        </button>
                      ) : 'Lead Source'}
                    </th>
                    <th className="text-center py-3 px-3 text-gray-400 font-medium">
                      <button onClick={() => { setSortField('date'); setSortOrder(prev => sortField === 'date' ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'); }} className="inline-flex items-center gap-1 hover:text-white transition-colors" data-testid="sort-close-date">
                        Close Date {sortField === 'date' && (sortOrder === 'asc' ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />)}
                      </button>
                    </th>
                    {(isProcessor || viewingProcessor) && <th className="text-center py-3 px-3 text-gray-400 font-medium">Funding Date</th>}
                    <th className="text-center py-3 px-3 text-gray-400 font-medium">
                      <button onClick={() => { setSortField('status'); setSortOrder(prev => sortField === 'status' ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'); }} className="inline-flex items-center gap-1 hover:text-white transition-colors" data-testid="sort-status">
                        Status {sortField === 'status' && (sortOrder === 'asc' ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />)}
                      </button>
                    </th>
                    {!(isProcessor || viewingProcessor) && (
                      <th className="text-right py-3 px-3 text-gray-400 font-medium">
                        <button onClick={() => { setSortField('commission'); setSortOrder(prev => sortField === 'commission' ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'); }} className="inline-flex items-center gap-1 hover:text-white transition-colors ml-auto" data-testid="sort-commission">
                          Commission {sortField === 'commission' && (sortOrder === 'asc' ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />)}
                        </button>
                      </th>
                    )}
                    {(isProcessor || viewingProcessor) && <th className="text-left py-3 px-3 text-gray-400 font-medium">Notes</th>}
                    <th className="text-center py-3 px-3 text-gray-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {monthLoans.map((loan, index) => (
                    <tr
                      key={loan.id}
                      className={`border-b border-[#222] hover:bg-[#1a1a1a] transition-colors ${index % 2 === 0 ? 'bg-[#0d0d0d]' : ''} ${loan.status === 'Withdrawn' ? 'opacity-50' : ''}`}
                    >
                      <td className="py-3 px-3">
                        <div>
                          <p className="text-white font-medium">{loan.clientName}</p>
                          {!(isProcessor || viewingProcessor) && loan.isSelfGenerated && (
                            <Badge className="bg-green-900/50 text-green-400 border border-green-800 text-[10px] mt-1">Self-Gen</Badge>
                          )}
                          {(isProcessor || viewingProcessor) && loan.userName && (
                            <p className="text-xs text-gray-500 mt-0.5">{loan.userName}</p>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-3 text-gray-300">{loan.loanNumber}</td>
                      <td className="py-3 px-3 text-white text-right font-medium">${loan.loanAmount.toLocaleString()}</td>
                      <td className="py-3 px-3 text-gray-300">{loan.lender || '—'}</td>
                      <td className="py-3 px-3 text-gray-300">{(isProcessor || viewingProcessor) ? (loan.processor || '—') : (loan.leadSource || '—')}</td>
                      <td className="py-3 px-3 text-center text-gray-300">
                        {(isProcessor || viewingProcessor) ? (
                          <button
                            onClick={() => handleDateEdit(loan, 'date', 'Closing Date')}
                            className="text-gray-300 hover:text-teal-400 transition-colors underline decoration-dotted underline-offset-2"
                            data-testid={`edit-close-date-${loan.id}`}
                          >
                            {formatDate(loan.date)}
                          </button>
                        ) : formatDate(loan.date)}
                      </td>
                      {(isProcessor || viewingProcessor) && (
                        <td className="py-3 px-3 text-center text-gray-300">
                          <button
                            onClick={() => handleDateEdit(loan, 'funding_date', 'Funding Date')}
                            className="text-gray-300 hover:text-teal-400 transition-colors underline decoration-dotted underline-offset-2"
                            data-testid={`edit-funding-date-${loan.id}`}
                          >
                            {loan.fundingDate ? formatDate(loan.fundingDate) : '—'}
                          </button>
                        </td>
                      )}
                      <td className="py-3 px-3 text-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleStatusClick(loan)}
                          data-testid={`status-btn-${loan.id}`}
                          className={`text-xs h-7 ${
                            loan.status === 'Closed' 
                              ? 'bg-green-900/30 text-green-400 border-green-800 hover:bg-green-900/50' 
                              : loan.status === 'Withdrawn'
                              ? 'bg-red-900/30 text-red-400 border-red-800 hover:bg-red-900/50'
                              : 'bg-amber-900/30 text-amber-400 border-amber-800 hover:bg-amber-900/50'
                          }`}
                        >
                          {loan.status === 'Pending' ? 'In Process' : loan.status}
                        </Button>
                      </td>
                      {!(isProcessor || viewingProcessor) && (
                      <td className="py-3 px-3 text-right">
                        {loan.status === 'Closed' ? (
                          <span className="text-green-400 font-semibold">${calculateCommission(loan).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        ) : loan.status === 'In Process' || loan.status === 'Pending' ? (
                          <span className="text-amber-400 font-medium">${calculateEstimatedCommission(loan).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        ) : (
                          <span className="text-gray-500">—</span>
                        )}
                      </td>
                      )}
                      {(isProcessor || viewingProcessor) && (
                      <td className="py-3 px-3">
                        {loan.notes ? (
                          <span className="text-gray-400 text-xs line-clamp-2" title={loan.notes}>
                            <MessageSquare className="w-3 h-3 inline mr-1 text-teal-500" />
                            {loan.notes.length > 40 ? loan.notes.substring(0, 40) + '...' : loan.notes}
                          </span>
                        ) : (
                          <span className="text-gray-600 text-xs">—</span>
                        )}
                      </td>
                      )}
                      <td className="py-3 px-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditLoan(loan)}
                            className="text-gray-400 hover:text-white h-8 w-8"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                          {!(isProcessor || viewingProcessor) && isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteConfirmDialog({ open: true, loanId: loan.id, clientName: loan.clientName })}
                            className="text-gray-400 hover:text-red-400 h-8 w-8"
                            data-testid={`delete-loan-${loan.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-[#333] bg-[#1a1a1a]">
                    <td colSpan="2" className="py-3 px-3 text-white font-semibold">Total ({monthLoans.length} loan{monthLoans.length !== 1 ? 's' : ''})</td>
                    <td className="py-3 px-3 text-white text-right font-semibold">${monthLoans.reduce((s, l) => s + l.loanAmount, 0).toLocaleString()}</td>
                    <td colSpan={(isProcessor || viewingProcessor) ? "4" : "4"}></td>
                    {!(isProcessor || viewingProcessor) && (
                    <td className="py-3 px-3 text-green-400 text-right font-semibold">${monthlyStats.totalCommission.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    )}
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Funding Date Dialog */}
      <Dialog open={fundingDateDialog.open} onOpenChange={(open) => {
        if (!open) {
          setFundingDateDialog({ open: false, loanId: null, fundingDate: '', closingDate: '' });
        }
      }}>
        <DialogContent className="bg-[#111] border-[#222] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Confirm Closing & Funding</DialogTitle>
            <DialogDescription className="text-gray-400">
              Confirm the closing date and enter the funding date to mark this loan as closed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="closingDate" className="text-gray-300">Closing Date</Label>
              <Input
                id="closingDate"
                type="date"
                data-testid="closing-date-input"
                value={fundingDateDialog.closingDate}
                onChange={(e) => setFundingDateDialog({ ...fundingDateDialog, closingDate: e.target.value })}
                className="bg-[#1a1a1a] border-[#333] text-white focus:border-green-500"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fundingDate" className="text-gray-300">Funding Date</Label>
              <Input
                id="fundingDate"
                type="date"
                data-testid="funding-date-input"
                value={fundingDateDialog.fundingDate}
                onChange={(e) => setFundingDateDialog({ ...fundingDateDialog, fundingDate: e.target.value })}
                className="bg-[#1a1a1a] border-[#333] text-white focus:border-green-500"
              />
            </div>
          </div>
          <div className="flex space-x-3">
            <Button
              onClick={handleFundingDateSubmit}
              data-testid="mark-as-closed-btn"
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
            >
              Mark as Closed
            </Button>
            <Button
              variant="outline"
              onClick={() => setFundingDateDialog({ open: false, loanId: null, fundingDate: '', closingDate: '' })}
              className="border-[#333] text-gray-300 hover:bg-[#1a1a1a]"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Status Picker Dialog */}
      <Dialog open={statusPickerDialog.open} onOpenChange={(open) => !open && setStatusPickerDialog({ open: false, loanId: null, currentStatus: '' })}>
        <DialogContent className="bg-[#111] border-[#333] text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Update Loan Status</DialogTitle>
            <DialogDescription className="text-gray-400">Select a new status for this loan</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 mt-2">
            {['In Process', 'Closed', 'Withdrawn'].map(status => {
              const isActive = statusPickerDialog.currentStatus === status || 
                (statusPickerDialog.currentStatus === 'Pending' && status === 'In Process');
              return (
                <Button
                  key={status}
                  variant="outline"
                  data-testid={`status-option-${status.toLowerCase().replace(/\s/g, '-')}`}
                  onClick={() => handleStatusSelect(status)}
                  className={`justify-start h-12 text-sm font-medium transition-all ${
                    status === 'In Process'
                      ? isActive 
                        ? 'bg-amber-900/50 text-amber-400 border-amber-600 ring-2 ring-amber-500/30'
                        : 'bg-amber-900/20 text-amber-400 border-amber-800 hover:bg-amber-900/40'
                    : status === 'Closed'
                      ? isActive
                        ? 'bg-green-900/50 text-green-400 border-green-600 ring-2 ring-green-500/30'
                        : 'bg-green-900/20 text-green-400 border-green-800 hover:bg-green-900/40'
                    : isActive
                        ? 'bg-red-900/50 text-red-400 border-red-600 ring-2 ring-red-500/30'
                        : 'bg-red-900/20 text-red-400 border-red-800 hover:bg-red-900/40'
                  }`}
                >
                  <div className={`w-2.5 h-2.5 rounded-full mr-3 ${
                    status === 'In Process' ? 'bg-amber-400' : status === 'Closed' ? 'bg-green-400' : 'bg-red-400'
                  }`} />
                  {status}
                  {isActive && <span className="ml-auto text-xs opacity-60">Current</span>}
                </Button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Processor Date Edit Dialog */}
      <Dialog open={dateEditDialog.open} onOpenChange={(open) => !open && setDateEditDialog({ open: false, loanId: null, field: '', value: '', label: '' })}>
        <DialogContent className="bg-[#111] border-[#222] text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white text-base">Edit {dateEditDialog.label}</DialogTitle>
            <DialogDescription className="text-gray-400 text-xs">Update the {dateEditDialog.label?.toLowerCase()} for this loan</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label className="text-gray-300">{dateEditDialog.label}</Label>
              <Input
                type="date"
                value={dateEditDialog.value}
                onChange={(e) => setDateEditDialog({ ...dateEditDialog, value: e.target.value })}
                className="bg-[#1a1a1a] border-[#333] text-white focus:border-teal-500"
                data-testid="date-edit-input"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setDateEditDialog({ open: false, loanId: null, field: '', value: '', label: '' })}
                className="border-[#333] text-gray-400 hover:text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDateEditSubmit}
                className="bg-teal-600 hover:bg-teal-700 text-white"
                data-testid="date-edit-save"
              >
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Grid Picker Dialogs */}
      {/* Lender Picker */}
      <Dialog open={pickerOpen === 'lender'} onOpenChange={(open) => !open && setPickerOpen(null)}>
        <DialogContent className="bg-[#111] border-[#222] text-white max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-white text-base">Select Lender</DialogTitle>
            <DialogDescription className="text-gray-400 text-xs">Choose a lender for this loan</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-1.5 pt-1">
            {lenderOptions.map(lender => (
              <button
                key={lender}
                onClick={() => {
                  setFormData({ ...formData, lender });
                  if (formErrors.lender) setFormErrors({ ...formErrors, lender: '' });
                  setPickerOpen(null);
                }}
                className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-all text-center ${
                  formData.lender === lender ? 'bg-green-900/40 text-green-400 border border-green-700 ring-1 ring-green-500/20' : 'bg-[#1a1a1a] border border-[#333] text-gray-300 hover:border-green-600 hover:bg-[#1e1e1e]'
                }`}
                data-testid={`lender-option-${lender.replace(/\s/g, '-').toLowerCase()}`}
              >
                {lender}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Lead Source Picker */}
      <Dialog open={pickerOpen === 'leadSource'} onOpenChange={(open) => !open && setPickerOpen(null)}>
        <DialogContent className="bg-[#111] border-[#222] text-white max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-white text-base">Select Lead Source</DialogTitle>
            <DialogDescription className="text-gray-400 text-xs">Choose the lead source for this loan</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-1.5 pt-1">
            {leadSourceOptions.map(source => (
              <button
                key={source}
                onClick={() => {
                  const isReferral = source === 'Referral';
                  setFormData({
                    ...formData,
                    leadSource: source,
                    isSelfGenerated: isReferral,
                    referralSourceType: isReferral ? formData.referralSourceType : '',
                    referrerName: isReferral ? formData.referrerName : '',
                    referrerPhone: isReferral ? formData.referrerPhone : '',
                  });
                  if (formErrors.leadSource) setFormErrors({ ...formErrors, leadSource: '' });
                  setPickerOpen(null);
                }}
                className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-all text-center ${
                  formData.leadSource === source ? 'bg-green-900/40 text-green-400 border border-green-700 ring-1 ring-green-500/20' : 'bg-[#1a1a1a] border border-[#333] text-gray-300 hover:border-green-600 hover:bg-[#1e1e1e]'
                }`}
                data-testid={`leadsource-option-${source.replace(/\s/g, '-').toLowerCase()}`}
              >
                {source}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Processor Picker */}
      <Dialog open={pickerOpen === 'processor'} onOpenChange={(open) => !open && setPickerOpen(null)}>
        <DialogContent className="bg-[#111] border-[#222] text-white max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-white text-base">Select Processor</DialogTitle>
            <DialogDescription className="text-gray-400 text-xs">Choose a processor for this loan</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-1.5 pt-1">
            {processorOptions.map(proc => (
              <button
                key={proc}
                onClick={() => {
                  setFormData({ ...formData, processor: proc });
                  if (formErrors.processor) setFormErrors({ ...formErrors, processor: '' });
                  setPickerOpen(null);
                }}
                className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-all text-center ${
                  formData.processor === proc ? 'bg-teal-900/40 text-teal-400 border border-teal-700 ring-1 ring-teal-500/20' : 'bg-[#1a1a1a] border border-[#333] text-gray-300 hover:border-teal-600 hover:bg-[#1e1e1e]'
                }`}
                data-testid={`processor-option-${proc.replace(/\s/g, '-').toLowerCase()}`}
              >
                {proc}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* State Picker */}
      <Dialog open={pickerOpen === 'state'} onOpenChange={(open) => !open && setPickerOpen(null)}>
        <DialogContent className="bg-[#111] border-[#222] text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white text-base">Select State</DialogTitle>
            <DialogDescription className="text-gray-400 text-xs">Choose the state for this loan</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-8 gap-1.5 pt-1">
            {stateOptions.map(st => (
              <button
                key={st}
                onClick={() => {
                  setFormData({ ...formData, state: st });
                  if (formErrors.state) setFormErrors({ ...formErrors, state: '' });
                  setPickerOpen(null);
                }}
                className={`px-2 py-2 rounded-lg text-sm font-bold transition-all text-center ${
                  formData.state === st ? 'bg-green-900/40 text-green-400 border border-green-700 ring-1 ring-green-500/20' : 'bg-[#1a1a1a] border border-[#333] text-gray-300 hover:border-green-600 hover:bg-[#1e1e1e]'
                }`}
                data-testid={`state-option-${st.toLowerCase()}`}
              >
                {st}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Loan Type Picker */}
      <Dialog open={pickerOpen === 'loanType'} onOpenChange={(open) => !open && setPickerOpen(null)}>
        <DialogContent className="bg-[#111] border-[#222] text-white max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-white text-base">Select Loan Type</DialogTitle>
            <DialogDescription className="text-gray-400 text-xs">Choose the loan type</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-1.5 pt-1">
            {loanTypeOptions.map(type => (
              <button
                key={type}
                onClick={() => {
                  setFormData({ ...formData, loanType: type });
                  if (formErrors.loanType) setFormErrors({ ...formErrors, loanType: '' });
                  setPickerOpen(null);
                }}
                className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-all text-center ${
                  formData.loanType === type ? 'bg-green-900/40 text-green-400 border border-green-700 ring-1 ring-green-500/20' : 'bg-[#1a1a1a] border border-[#333] text-gray-300 hover:border-green-600 hover:bg-[#1e1e1e]'
                }`}
                data-testid={`loantype-option-${type.replace(/\s/g, '-').toLowerCase()}`}
              >
                {type}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Referral Source Type Picker */}
      <Dialog open={pickerOpen === 'referralSourceType'} onOpenChange={(open) => !open && setPickerOpen(null)}>
        <DialogContent className="bg-[#111] border-[#222] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-base">Select Referral Source Type</DialogTitle>
            <DialogDescription className="text-gray-400 text-xs">How was this client referred?</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-1.5 pt-1">
            {['Real Estate Agent', 'Past Client', 'Family', 'Friend', 'Builder', 'Other'].map(type => (
              <button
                key={type}
                onClick={() => {
                  setFormData({ ...formData, referralSourceType: type });
                  if (formErrors.referralSourceType) setFormErrors({ ...formErrors, referralSourceType: '' });
                  setPickerOpen(null);
                }}
                className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-all text-center ${
                  formData.referralSourceType === type ? 'bg-amber-900/40 text-amber-400 border border-amber-700 ring-1 ring-amber-500/20' : 'bg-[#1a1a1a] border border-[#333] text-gray-300 hover:border-amber-600 hover:bg-[#1e1e1e]'
                }`}
                data-testid={`referral-type-option-${type.replace(/\s/g, '-').toLowerCase()}`}
              >
                {type}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmDialog.open} onOpenChange={(open) => !open && setDeleteConfirmDialog({ open: false, loanId: null, clientName: '' })}>
        <DialogContent className="bg-[#1a1a1a] border-red-900/50 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-400 text-lg">Confirm Loan Deletion</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-gray-300 text-sm">
              Are you sure you want to delete the loan for <span className="text-white font-semibold">{deleteConfirmDialog.clientName}</span>?
            </p>
            <div className="bg-red-950/30 border border-red-900/50 rounded-lg p-3">
              <p className="text-red-300 text-xs font-medium">This action will be logged with your name and timestamp.</p>
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirmDialog({ open: false, loanId: null, clientName: '' })}
                className="border-[#333] text-gray-300 hover:bg-[#222]"
                data-testid="delete-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleDeleteLoan(deleteConfirmDialog.loanId)}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="delete-confirm-btn"
              >
                Delete Loan
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoanManagement;
